// D3k: scheduled-for picker helpers. Pure module — no React, no DOM.
// Implements the locked design from the dev-sync clarify ("1A, 2A, 3A"):
//
//   1A. WINDOW SET — 5 modes: morning / afternoon / specific_window
//       (start+end time pickers) / specific_time (single time picker) /
//       date_only. Covers the user's stated "Some industries may have a
//       real target time, but our industry is generally time blocks"
//       case + their four concrete examples.
//
//   2A. PERSISTENCE — new `scheduled_window_label text` column on
//       `logi_routes`. `scheduled_for timestamptz` stays as the
//       canonical sort timestamp, derived here from the picker state
//       (Morning → 09:00 local, Afternoon → 13:00 local, Specific window
//       → start time, Specific time → as-typed, Date only → 08:00 local).
//
//   3A. BACKFILL — none. Existing routes get scheduled_window_label =
//       null and continue to render the formatted timestamp.
//
// Timezone semantics: the picker uses the dispatcher's browser local
// time as a proxy for the dispatch location's local time. The single-
// timezone pilot org makes this safe; multi-timezone routing is a
// future concern that would either store the dispatch-side timezone on
// the route or anchor labels to UTC.

export type SchedulingMode =
  | "morning"
  | "afternoon"
  | "specific_window"
  | "specific_time"
  | "date_only";

export type SchedulingDraft = {
  mode: SchedulingMode;
  // "YYYY-MM-DD" from <input type="date">. Required for every mode (even
  // morning / afternoon need a date to anchor the canonical timestamp).
  // Empty string is the "user hasn't picked a date yet" state — treated
  // as "skip scheduling" by deriveSchedulingFromDraft.
  date: string;
  // "HH:MM" 24h from <input type="time">. Only consulted for
  // specific_time + specific_window (start).
  startTime: string;
  // "HH:MM" 24h. Only consulted for specific_window.
  endTime: string;
};

export type DerivedScheduling = {
  // ISO 8601 with millisecond precision and a 'Z' suffix — what
  // `logi_routes.scheduled_for` (timestamptz) stores. Always
  // non-null when this object exists; the picker yields null on the
  // outer shape when no date was picked at all (see deriveSchedulingFromDraft).
  scheduled_for: string;
  // null when scheduled_for is the truth on its own (specific_time mode).
  // Otherwise a human-readable label like "Morning", "8:00–10:00 AM",
  // "Date only".
  scheduled_window_label: string | null;
};

export const DEFAULT_DRAFT: SchedulingDraft = {
  mode: "date_only",
  date: "",
  startTime: "",
  endTime: "",
};

// Anchor hours used to derive a canonical sort timestamp for the
// "vague" modes. Picked to be roughly representative of when a
// dispatcher would expect that block to start. Date-only sits at 08:00
// so it sorts to start-of-business-day, ahead of morning routes that
// might fire at 9.
const ANCHOR_HOUR_BY_MODE: Record<SchedulingMode, number> = {
  morning: 9,
  afternoon: 13,
  specific_window: 0, // unused — pulled from startTime
  specific_time: 0, // unused — pulled from startTime
  date_only: 8,
};

const MODE_HUMAN_LABEL: Record<SchedulingMode, string> = {
  morning: "Morning",
  afternoon: "Afternoon",
  specific_window: "Specific window",
  specific_time: "Specific time",
  date_only: "Date only",
};

export function modeLabel(mode: SchedulingMode): string {
  return MODE_HUMAN_LABEL[mode];
}

// Parse a "YYYY-MM-DD" string into a {year, month0, day} tuple. month0
// is zero-indexed to match the Date constructor. Returns null on any
// malformed input so the caller can surface a clean validation error
// instead of producing a NaN-laden Date.
function parseDateString(date: string): { year: number; month0: number; day: number } | null {
  const m = date.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const year = Number(m[1]);
  const month1 = Number(m[2]);
  const day = Number(m[3]);
  if (year < 1900 || year > 9999) return null;
  if (month1 < 1 || month1 > 12) return null;
  if (day < 1 || day > 31) return null;
  return { year, month0: month1 - 1, day };
}

// Parse "HH:MM" 24h into {hour, minute}. Returns null on malformed input.
function parseTimeString(time: string): { hour: number; minute: number } | null {
  const m = time.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const hour = Number(m[1]);
  const minute = Number(m[2]);
  if (hour < 0 || hour > 23) return null;
  if (minute < 0 || minute > 59) return null;
  return { hour, minute };
}

// Compose a local-time JS Date and convert to ISO. The Date constructor's
// (year, month0, day, hour, minute) form treats the inputs as the
// runtime's local timezone — exactly what we want for the dispatcher-as-
// proxy-for-dispatch-location semantic. Returns null when the parts are
// invalid.
function localDateToISO(
  date: string,
  hour: number,
  minute: number
): string | null {
  const parts = parseDateString(date);
  if (!parts) return null;
  const d = new Date(parts.year, parts.month0, parts.day, hour, minute, 0, 0);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

// Format an "HH:MM" 24h string as "8:00 AM" / "1:30 PM" for inclusion in
// a human-readable window label. Tolerant of malformed input (returns
// the original string unchanged) so the caller can render whatever the
// user typed without exploding.
export function format12HourTime(time: string): string {
  const parts = parseTimeString(time);
  if (!parts) return time;
  const ampm = parts.hour >= 12 ? "PM" : "AM";
  const hour12 = parts.hour === 0 ? 12 : parts.hour > 12 ? parts.hour - 12 : parts.hour;
  const mm = String(parts.minute).padStart(2, "0");
  return `${hour12}:${mm} ${ampm}`;
}

// Format the human-readable window label for "specific window" mode.
// Uses an em-dash to match the user's "8-10am" shorthand more closely
// without mangling the AM/PM split. Drops the AM/PM on the start when
// both halves share it (e.g. "8:00–10:00 AM" rather than
// "8:00 AM–10:00 AM").
export function formatWindowRangeLabel(startTime: string, endTime: string): string {
  const startParts = parseTimeString(startTime);
  const endParts = parseTimeString(endTime);
  const startFull = format12HourTime(startTime);
  const endFull = format12HourTime(endTime);
  if (!startParts || !endParts) return `${startFull}–${endFull}`;
  const startAmPm = startParts.hour >= 12 ? "PM" : "AM";
  const endAmPm = endParts.hour >= 12 ? "PM" : "AM";
  if (startAmPm === endAmPm) {
    // "8:00–10:00 AM" — strip the redundant AM on the start half.
    const startNoAmPm = startFull.replace(/ (AM|PM)$/, "");
    return `${startNoAmPm}–${endFull}`;
  }
  return `${startFull}–${endFull}`;
}

// Main derivation. Returns { scheduled_for, scheduled_window_label } from
// a SchedulingDraft, or null when no scheduling is set at all (no date
// picked — treat as "no schedule yet"), or an error string for inputs
// that are partially filled in a way the picker can't make sense of
// (e.g. specific_time mode with no startTime).
//
// Return shape:
//   { ok: true, value: null } — no scheduling (date is empty)
//   { ok: true, value: DerivedScheduling } — picker had enough to derive
//   { ok: false, error: string } — invalid input that the form should reject
export function deriveSchedulingFromDraft(
  draft: SchedulingDraft
): { ok: true; value: DerivedScheduling | null } | { ok: false; error: string } {
  const date = draft.date.trim();
  if (!date) return { ok: true, value: null };
  if (!parseDateString(date)) {
    return { ok: false, error: "Date must be in YYYY-MM-DD format." };
  }

  switch (draft.mode) {
    case "morning":
    case "afternoon":
    case "date_only": {
      const anchor = ANCHOR_HOUR_BY_MODE[draft.mode];
      const iso = localDateToISO(date, anchor, 0);
      if (!iso) return { ok: false, error: "Could not compute schedule timestamp." };
      return {
        ok: true,
        value: {
          scheduled_for: iso,
          scheduled_window_label: MODE_HUMAN_LABEL[draft.mode],
        },
      };
    }
    case "specific_time": {
      const startTime = draft.startTime.trim();
      if (!startTime) {
        return { ok: false, error: "Specific time requires a time." };
      }
      const tp = parseTimeString(startTime);
      if (!tp) return { ok: false, error: "Time must be in HH:MM 24-hour format." };
      const iso = localDateToISO(date, tp.hour, tp.minute);
      if (!iso) return { ok: false, error: "Could not compute schedule timestamp." };
      // specific_time leaves the label null — the canonical timestamp IS
      // the human-readable value, and the display formatter will render
      // it directly so no duplication.
      return { ok: true, value: { scheduled_for: iso, scheduled_window_label: null } };
    }
    case "specific_window": {
      const startTime = draft.startTime.trim();
      const endTime = draft.endTime.trim();
      if (!startTime || !endTime) {
        return { ok: false, error: "Specific window requires both a start and end time." };
      }
      const startParts = parseTimeString(startTime);
      const endParts = parseTimeString(endTime);
      if (!startParts || !endParts) {
        return { ok: false, error: "Times must be in HH:MM 24-hour format." };
      }
      const startMinutes = startParts.hour * 60 + startParts.minute;
      const endMinutes = endParts.hour * 60 + endParts.minute;
      if (endMinutes <= startMinutes) {
        return { ok: false, error: "End time must be after start time." };
      }
      const iso = localDateToISO(date, startParts.hour, startParts.minute);
      if (!iso) return { ok: false, error: "Could not compute schedule timestamp." };
      return {
        ok: true,
        value: {
          scheduled_for: iso,
          scheduled_window_label: formatWindowRangeLabel(startTime, endTime),
        },
      };
    }
    default: {
      const _exhaustive: never = draft.mode;
      void _exhaustive;
      return { ok: false, error: `Unknown scheduling mode: ${String(draft.mode)}` };
    }
  }
}

// Format a route's stored scheduling for display. Prefers the
// human-readable label when present (Morning / Afternoon / 8:00–10:00 AM /
// Date only); falls back to the localized timestamp when only
// scheduled_for is set (legacy rows + specific_time mode); returns null
// when nothing is scheduled.
export function formatScheduledForDisplay(args: {
  scheduled_for: string | null;
  scheduled_window_label: string | null;
}): string | null {
  const { scheduled_for, scheduled_window_label } = args;
  if (!scheduled_for && !scheduled_window_label) return null;
  if (scheduled_window_label && scheduled_for) {
    // Date pulled from the LOCAL representation of scheduled_for, so
    // "Jun 5, 2026 · Morning" reads correctly in the dispatcher's tz.
    try {
      const d = new Date(scheduled_for);
      const datePart = d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
      return `${datePart} · ${scheduled_window_label}`;
    } catch {
      return scheduled_window_label;
    }
  }
  if (scheduled_window_label) return scheduled_window_label;
  // Legacy / specific_time path: render the full localized timestamp.
  try {
    return new Date(scheduled_for as string).toLocaleString();
  } catch {
    return scheduled_for;
  }
}
